import { useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  generalInfoQuery,
  consolidatedInsightsByGameTypeQuery,
} from '../api';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';

import './styles.css';

// Section-label normalization. Backend may return variants (with or
// without " Section" suffix, singular/plural profits, etc.); each
// entry's `match` array normalizes those to a single canonical label.
const BALANCE_SHEET_SECTIONS = [
  { label: 'Current Assets',          match: ['current assets', 'current assets section'] },
  { label: 'Non-Current Assets',      match: ['non-current assets', 'non-current assets section', 'long-term assets', 'long-term assets section'] },
  { label: 'Current Liabilities',     match: ['current liabilities', 'current liabilities section'] },
  { label: 'Non-Current Liabilities', match: ['non-current liabilities', 'non-current liabilities section', 'long-term liabilities', 'long-term liabilities section'] },
  { label: 'Equity',                  match: ['equity', 'equity section'] },
];

const INCOME_STATEMENT_SECTIONS = [
  { label: 'Gross Profits',         match: ['gross profit', 'gross profit section', 'gross profits', 'gross profits section'] },
  { label: 'Operating Profits',     match: ['operating profit', 'operating profit section', 'operating profits', 'operating profits section', 'operating expenses', 'operating expenses section'] },
  { label: 'Non-Operating Profits', match: ['non-operating', 'non-operating section', 'non-operating profits', 'non-operating profits section', 'other income/expenses', 'other income or expenses'] },
];

// Account-level inclusion threshold. An account qualifies if at least
// one misclassified section has >= this many students. Summing across
// sections would double-count students, because each student sorts
// every account twice (once on the balance sheet, once on the multi-
// step income statement), so the same student can appear in two
// section counts for the same account. Ranking uses the highest
// single-section count as the primary key for the same reason.
const SECTION_INCLUSION_THRESHOLD = 5;
const TOP_N = 10;

const norm = (s) => (s || '').toString().trim().toLowerCase();

// Map a raw backend section name to its canonical label. Returns null
// for anything outside the classified-balance-sheet / multi-step
// income-statement label set (e.g. Level-1 "Balance Sheet" /
// "Income Statement" options — those aren't sections).
function resolveSectionLabel(raw) {
  const n = norm(raw);
  const bs = BALANCE_SHEET_SECTIONS.find(s => s.match.includes(n));
  if (bs) return bs.label;
  const is = INCOME_STATEMENT_SECTIONS.find(s => s.match.includes(n));
  if (is) return is.label;
  return null;
}

// Build the top-10 account ranking from the raw insights payload.
// Each `insight` groups one account (`question`) with per-section
// option rows carrying `option`, `is_correct`, and `count_users`.
function buildRanking(insights) {
  const rows = [];
  insights.forEach((insight) => {
    if (!insight?.question) return;

    let home = null;
    const misclassifications = [];

    (insight.options || []).forEach((opt) => {
      const label = resolveSectionLabel(opt.option);
      if (!label) return;
      const count = opt.count_users || 0;
      if (opt.is_correct) {
        home = label;
        return;
      }
      if (count > 0) {
        misclassifications.push({ label, count });
      }
    });

    if (!home) return; // Guard: account with no correct answer row is unusable.

    misclassifications.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

    // Rank + inclusion key = the largest single-section misclassification.
    // Summing across sections would double-count students (see comment
    // on SECTION_INCLUSION_THRESHOLD above), so we use max instead.
    const peak = misclassifications.length ? misclassifications[0].count : 0;
    if (peak < SECTION_INCLUSION_THRESHOLD) return;

    rows.push({
      account: insight.question,
      home,
      misclassifications,
      peak,
    });
  });

  rows.sort((a, b) => b.peak - a.peak || a.account.localeCompare(b.account));
  return rows.slice(0, TOP_N);
}

function AccountRow({ rank, row }) {
  return (
    <li className="misclassified-account-row">
      <span className="misclassified-account-rank">{rank}</span>
      <div className="misclassified-account-main">
        <div className="misclassified-account-name">{row.account}</div>
        <p className="misclassified-account-belongs">
          Belongs in <b>{row.home.toLowerCase()}</b>
        </p>
      </div>
      <ul className="misclassified-account-sections">
        {row.misclassifications.map((m, i) => (
          <li
            key={`${m.label}-${i}`}
            className={i === 0 ? 'misclassified-account-section is-top' : 'misclassified-account-section'}
          >
            {m.label} <span className="misclassified-account-section-count">({m.count})</span>
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function InstructorInsightsClassifiedMultistep() {
  const navigate = useNavigate();
  const location = useLocation();

  const direction = location.state?.direction === 'back' ? 'back' : 'forward';

  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};
  const { data: consolidatedInsights } = useQuery(
    consolidatedInsightsByGameTypeQuery({ gameType: 'classification' })
  );

  const games = generalInfo?.games;

  const classificationInsights = useMemo(() => {
    if (!consolidatedInsights) return [];
    if (Array.isArray(consolidatedInsights)) return consolidatedInsights;
    return consolidatedInsights.classification || [];
  }, [consolidatedInsights]);

  const topAccounts = useMemo(
    () => buildRanking(classificationInsights),
    [classificationInsights]
  );

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/decision-insights/classifying-normal');
  };

  const resolveNextUrl = () => {
    if (games && games.length > 0) {
      if (games.some(game => game.game_type === 'Balanced Equation')) {
        return '/instructor/insights/decision-insights/double-entry';
      }
      if (games.some(game => game.game_type === 'The Principles')) {
        return '/instructor/insights/decision-insights/principles';
      }
    }
    return '/instructor/insights/progress-scores/progress';
  };

  const handleNextButtonClick = () => {
    navigate(resolveNextUrl());
  };

  // Skip the page when the course has no classified-multistep session
  // OR when no account crosses the account-level inclusion threshold.
  // Falls back to the sibling page that fits the traversal direction.
  const sessions = generalInfo?.sessions;
  const hasClassifiedMultistepSession = useMemo(() => {
    if (!sessions) return null;
    return sessions.some(session =>
      Object.values(session.level_names || {}).includes('classified-multistep')
    );
  }, [sessions]);

  const skippedRef = useRef(false);
  useEffect(() => {
    if (skippedRef.current) return;
    if (!consolidatedInsights) return;
    if (hasClassifiedMultistepSession === null) return;
    if (hasClassifiedMultistepSession && topAccounts.length > 0) return;
    skippedRef.current = true;
    const target = direction === 'back'
      ? '/instructor/insights/decision-insights/classifying-normal'
      : resolveNextUrl();
    navigate(target, { replace: true, state: { direction } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidatedInsights, topAccounts.length, hasClassifiedMultistepSession, direction]);

  return (
    <SubPagesLayout
      navigation={navigation}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container instructor-classified-multistep-content"
      showPagination={false}
    >
      <h1>INSIGHTS | OVERALL <b>| Classified &amp; Multi-Step</b></h1>
      <div className="misclassified-header">
        <div className="misclassified-header-copy">
          <h2 className="misclassified-title">Most Misclassified Accounts</h2>
          <p className="misclassified-description">
            These accounts were sorted to incorrect financial statement section.
          </p>
        </div>
        <div className="misclassified-header-buttons">
          <button className="popup-messages-button button-navi no-margin" onClick={handlePrevButtonClick}>Back</button>
          <button className="popup-messages-button button-red no-margin" onClick={handleNextButtonClick}>Next</button>
        </div>
      </div>

      <div className="misclassified-panel">
        <div className="misclassified-colhead">
          <span className="misclassified-colhead-rank" />
          <span className="misclassified-colhead-account">Account</span>
          <span className="misclassified-colhead-sections">Misclassified Sections (Student Count)</span>
        </div>
        <ol className="misclassified-list">
          {topAccounts.map((row, i) => (
            <AccountRow key={row.account} rank={i + 1} row={row} />
          ))}
        </ol>
      </div>
    </SubPagesLayout>
  );
}
